import * as z from 'zod'

import type { GetAdminLedgerParams } from '@/api/gen'
import { PAYOUT_METHODS } from '@/domain/payout-methods'

/**
 * URL search-param schema + serialization for /admin/ledger. Same philosophy as
 * `partners-search-params`: the URL is the single source of truth for filters,
 * sort, and page, so a teammate can share a filtered cut. `toGetAdminLedgerParams`
 * translates the validated route state into the Orval `GetAdminLedgerParams`
 * wire shape, keeping the translation in one place.
 */

// Mirrors gto-wizard `AffiliateLedgerReadinessStatus`. Sealed here so a stale
// URL value can't reach the BE call.
export const LEDGER_READINESS_STATUSES = [
  'MISSING_METHOD',
  'MISSING_DETAILS',
  'BELOW_MINIMUM',
  'PENDING_PAYOUT',
  'NO_PAYOUT_REQUESTED',
] as const
export type LedgerReadinessStatus = (typeof LEDGER_READINESS_STATUSES)[number]

// Mirrors gto-wizard `AffiliateLedgerSortField`.
export const LEDGER_SORT_FIELDS = [
  'balance_available',
  'balance_locked',
  'requested_payouts',
  'display_name',
] as const
export type LedgerSortField = (typeof LEDGER_SORT_FIELDS)[number]

export const LEDGER_SORT_DIRS = ['asc', 'desc'] as const
export type LedgerSortDir = (typeof LEDGER_SORT_DIRS)[number]

export const ledgerSearchSchema = z.object({
  readiness: z.enum(LEDGER_READINESS_STATUSES).optional(),
  method: z.enum(PAYOUT_METHODS).optional(),
  // Single toggle: `'yes'` = show zero-balance partners, absent = hide them.
  // A string sentinel (not a URL boolean) avoids the `z.coerce.boolean('false')
  // === true` footgun and matches the partners page's tri-state facets.
  includeZero: z.literal('yes').optional(),
  // Single toggle: `'yes'` = only partners who can be paid right now (valid
  // method + validated details + Open OR requested balance > 0), absent = all.
  // Same string-sentinel idiom as `includeZero`.
  payable: z.literal('yes').optional(),
  sort: z.enum(LEDGER_SORT_FIELDS).optional(),
  dir: z.enum(LEDGER_SORT_DIRS).optional(),
  page: z.coerce.number().int().positive().optional(),
})

export type LedgerSearch = z.infer<typeof ledgerSearchSchema>

export const DEFAULT_LEDGER_SEARCH: LedgerSearch = {
  readiness: undefined,
  method: undefined,
  includeZero: undefined,
  payable: undefined,
  sort: undefined,
  dir: undefined,
  page: undefined,
}

/**
 * Translate the URL state into the Orval `GetAdminLedgerParams` shape. Returns
 * only the keys that carry a value so the generated query key stays stable.
 */
export const toGetAdminLedgerParams = (
  search: LedgerSearch,
  defaultPageSize: number,
): GetAdminLedgerParams => {
  const params: GetAdminLedgerParams = {
    page: search.page ?? 1,
    page_size: defaultPageSize,
  }

  if (search.readiness !== undefined) {
    params.readiness_status = search.readiness
  }
  if (search.method !== undefined) {
    params.payout_method = search.method
  }
  if (search.includeZero === 'yes') {
    params.include_zero = true
  }
  if (search.payable === 'yes') {
    params.payable_only = true
  }
  if (search.sort !== undefined) {
    params.sort_by = search.sort
    params.sort_dir = search.dir ?? 'desc'
  }

  return params
}
